import { Link } from "@tanstack/react-router";
import { useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Testimonial } from "@/lib/site-content";
import { CLIENT_LOGOS } from "@/lib/site-content";

export function Container({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("mx-auto w-full max-w-[1200px] px-5 py-10 md:px-8 md:py-16", className)}>
      {children}
    </div>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
      {children}
    </p>
  );
}

export function Lead({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("max-w-[56ch] text-base leading-relaxed text-body md:text-[19px]", className)}>
      {children}
    </p>
  );
}

export function PrimaryLink({
  to,
  hash,
  children,
  className,
  onClick,
}: {
  to: string;
  hash?: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      hash={hash}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-[15px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:opacity-100",
        className,
      )}
    >
      {children} <span aria-hidden="true">&rarr;</span>
    </Link>
  );
}

export function GhostLink({
  to,
  hash,
  children,
  className,
  onClick,
}: {
  to: string;
  hash?: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      hash={hash}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold text-foreground transition-colors hover:border-primary hover:text-primary",
        className,
      )}
    >
      {children}
    </Link>
  );
}

export function TextLink({
  to,
  hash,
  children,
  onClick,
}: {
  to: string;
  hash?: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      hash={hash}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-sm text-[15px] font-semibold text-foreground transition-colors hover:text-primary focus-visible:underline"
    >
      {children} <span aria-hidden="true">&rarr;</span>
    </Link>
  );
}

export function Quote({ item, className }: { item: Testimonial; className?: string }) {
  return (
    <figure
      className={cn(
        "flex h-full flex-col justify-between rounded-lg border border-border bg-card p-6",
        className,
      )}
    >
      <blockquote className="text-[15px] leading-relaxed text-body">
        &ldquo;{item.quote}&rdquo;
      </blockquote>
      <figcaption className="mt-5 text-sm font-semibold text-ink">
        {item.name}
        <span className="block font-normal text-muted-foreground">{item.role}</span>
      </figcaption>
    </figure>
  );
}

export function LogoStrip() {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="border-t border-border bg-background">
      <Container className="py-8 md:py-10">
        <div className="grid grid-cols-[minmax(0,1fr)] items-center gap-4 md:flex md:flex-wrap md:gap-8">
          <h2
            id={headingId}
            className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase"
          >
            Working with
          </h2>
          <ul className="flex flex-wrap gap-x-7 gap-y-2 text-sm font-medium text-subtle">
            {CLIENT_LOGOS.map((logo) => (
              <li key={logo}>{logo}</li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

export function CtaBand({
  heading,
  body,
  to,
  cta,
  eyebrow,
  onCtaClick,
}: {
  heading: string;
  body: string;
  to: string;
  cta: string;
  eyebrow?: string;
  onCtaClick?: () => void;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="border-y border-border bg-surface">
      <Container>
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            <h2
              id={headingId}
              className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
            >
              {heading}
            </h2>
            <p className="mt-3 max-w-[62ch] text-base leading-relaxed text-body">{body}</p>
          </div>
          <PrimaryLink to={to} className="shrink-0" onClick={onCtaClick}>
            {cta}
          </PrimaryLink>
        </div>
      </Container>
    </section>
  );
}

/**
 * A short, self-contained answer placed directly under an H1. Answer engines
 * extract the first definitive paragraph on a page, so keep it 40-60 words and
 * make it true standing alone, without the surrounding page for context.
 */
export function DirectAnswer({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "max-w-[68ch] rounded-lg border-l-2 border-primary bg-surface px-5 py-4",
        className,
      )}
    >
      <p className="text-base leading-relaxed text-body">{children}</p>
    </div>
  );
}
